import {SerializedStyles} from '@emotion/react';
import {Chip, ChipProps, css, Typography, useTheme} from '@mui/material';
import React from 'react';
import {useIntl} from 'react-intl';
import {GetStylesFunction} from 'src/js/common/types';
import {TranslateUtils} from 'src/js/common/utils';
import {useMuiAnchor} from 'src/js/hooks';

import {DlMuiPopper} from './DlMuiPopper';

interface OwnProps extends ChipProps {
  inputCss?: SerializedStyles;
  title: string;
  big?: boolean;
  leftIcon?: JSX.Element;
  tooltip?: string;
  disabledTooltip?: string;
}

type Props = OwnProps;

export const DlMuiChipButton: React.FC<Props> = (props) => {
  const {inputCss, title, tooltip, big, leftIcon, disabled, disabledTooltip, ...restProps} = props;
  const theme = useTheme();
  const intl = useIntl();
  const styles = getStyles({theme, params: {big, inputCss}});

  const translatedLabel = TranslateUtils.translate(title, intl);
  const translatedTooltip = TranslateUtils.translate(disabled ? disabledTooltip ?? '' : tooltip ?? title, intl);
  const hasTooltip = !!translatedTooltip;

  const {mouseEventEnter, mouseEventLeave, open, anchorEl} = useMuiAnchor(hasTooltip);

  return (
    <div onMouseEnter={mouseEventEnter} onMouseLeave={mouseEventLeave}>
      <Chip
        data-name={'dl-mui-chip-button--chip'}
        size={'medium'}
        label={translatedLabel}
        variant={'outlined'}
        icon={leftIcon}
        disabled={disabled}
        {...restProps}
        css={styles.chip}
      />
      <DlMuiPopper hasTooltip={hasTooltip} anchorEl={anchorEl} open={open}>
        <Typography variant={'popper'}>{translatedTooltip}</Typography>
      </DlMuiPopper>
    </div>
  );
};

DlMuiChipButton.displayName = 'DlMuiChipButton';

const getStyles: GetStylesFunction = (options) => ({
  chip: css`
    height: ${options?.params?.big ? 48 : 26}px;
    padding: ${options?.params?.big ? '0 16px' : 0};
    border-radius: ${options?.params?.big ? 24 : 16}px;
    font-size: ${options?.params?.big ? 14 : 12}px;
    color: var(--dlChipButton);
    border-color: var(--dlChipButton);

    &:hover,
    &:focus {
      color: var(--dlChipButtonHover);
      border-color: var(--dlChipButtonHover);
    }

    &:active {
      color: var(--dlChipButtonActive);
      border-color: var(--dlChipButtonActive);
    }

    ${options?.params?.inputCss ?? ''};
  `,
});
